import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { StyleSheet, Pressable, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import Icon from '@/components/Icon';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import PropTypes from 'prop-types';
import { BORDER_RADIUS } from '@/constants/config';
import { NEUTRAL_ACCENT_IDS } from '@/constants/Colors';

const isWeb = Platform.OS === 'web';

// Same gate as ExploreCard / PromptCard: skip hover handlers on touch-only
// mobile web. Synthetic mouseenter on the first tap otherwise re-renders the
// row (hover highlight) and swallows the click, so chats need two taps.
const SUPPORTS_HOVER = (() => {
  if (!isWeb || typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  } catch {
    return false;
  }
})();

// Create styles outside component
const createStyles = (colors, isXS, subtitle, isActive, isWarning, noEllipsis) => StyleSheet.create({
  container: {
    width: '100%',
    height: subtitle ? (isXS ? 54 : 44) : (isXS ? 40 : 32),
    minHeight: subtitle ? (isXS ? 54 : 44) : (isXS ? 40 : 32),
    maxHeight: subtitle ? (isXS ? 54 : 44) : (isXS ? 40 : 32),
  },
  inner: {
    height: subtitle ? (isXS ? 56 : 44) : (isXS ? 40 : 32),
    minHeight: subtitle ? (isXS ? 56 : 44) : (isXS ? 40 : 32),
    maxHeight: subtitle ? (isXS ? 56 : 44) : (isXS ? 40 : 32),
    paddingLeft: 10,
    paddingRight: noEllipsis || isXS ? 10 : 6,
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  contentContainer: {
    height: '100%',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: 1,
    minWidth: 0,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
    maxHeight: subtitle ? 18 : undefined,
  },
  iconContainer: {
    height: 20,
    width: 20,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: BORDER_RADIUS.round,
    marginRight: isXS ? 10 : 8,
    flexShrink: 0,
  },
  title: {
    fontSize: 14.5,
    fontWeight: '425',
    flex: 1,
    color: isActive ? colors.accent_text_color : colors.text_color_primary,
    minWidth: 0,
    lineHeight: isXS ? 22 : 20,
  },
  subtitle: {
    fontSize: isXS ? 13 : 12,
    color: isWarning ? colors.text_color_primary : colors.text_color_secondary,
    marginTop: 2,
  },
  rightContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    transition: 'opacity 0.2s ease',
    flexShrink: 0,
    paddingLeft: 4,
  },
  active: {
    backgroundColor: colors.accent_color_primary,
  },
});

// Memoized icon component
const ItemIcon = React.memo(({ 
  icon, 
  customIcon, 
  isActive, 
  isWarning, 
  colors, 
  accentBackgroundColor,
  styles,
  isXS
}) => {
  if (!icon && !customIcon) return null;

  const useNeutralColor = isWarning || NEUTRAL_ACCENT_IDS.has(accentBackgroundColor);
  const inactiveColor = useNeutralColor ? colors.text_color_primary : colors.accent_color_primary;

  return (
    <View style={styles.iconContainer}>
      {customIcon || (
        <Icon
          name={icon}
          size={16}
          solid={isActive}
          color={isActive ? colors.accent_text_color : inactiveColor}
        />
      )}
    </View>
  );
});

ItemIcon.propTypes = {
  icon: PropTypes.string,
  customIcon: PropTypes.node,
  isActive: PropTypes.bool,
  isWarning: PropTypes.bool,
  colors: PropTypes.object.isRequired,
  accentBackgroundColor: PropTypes.string,
  styles: PropTypes.object.isRequired,
};

ItemIcon.displayName = 'ItemIcon';

// Memoized title component
const TitleContent = React.memo(({ 
  title, 
  subtitle, 
  isActive,  
  colors, 
  styles 
}) => (
  <View style={styles.contentContainer}>
    <View style={styles.titleContainer}>
      {typeof title === 'string' ? (
        <Text 
          numberOfLines={1}
          style={[
            styles.title,
            isActive && { color: colors.accent_text_color }
          ]}
        >
          {title}
        </Text>
      ) : (
        React.cloneElement(title, {
          style: [
            title.props.style,
            isActive && { color: colors.accent_text_color }
          ]
        })
      )}
    </View>
    {subtitle && (
      <Text numberOfLines={1} style={styles.subtitle}>
        {subtitle}
      </Text>
    )}
  </View>
));

TitleContent.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  subtitle: PropTypes.string,
  isActive: PropTypes.bool,
  colors: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
};

TitleContent.displayName = 'TitleContent';

// Memoized ellipsis button component
const EllipsisButton = React.memo(({ 
  isXS,
  isActive, 
  isOpen, 
  colors, 
  onPress,
  DropdownContent,
  onDropdownStateChange 
}) => {
  const handlePress = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onPress?.(e);
  }, [onPress]);

  const buttonStyle = useMemo(() => ({
    ...(isOpen && { 
      backgroundColor: isActive ? 'rgba(0, 0, 0, 0.2)' : colors.solid_color_tertiary 
    }),
    height: isXS ? 24 : 20,
    width: 24,
    borderRadius: 4,
  }), [isOpen, isActive, colors.solid_color_tertiary]);

  const ellipsisButton = (
    <Button
      icon="ellipsis"
      size='extraSmall'
      iconSize={12}
      onPress={handlePress}
      iconColor={isActive ? colors.accent_text_color : undefined}
      color={isActive ? colors.accent_text_color : undefined}
      hoverBackgroundColor={isActive ? 'rgba(0, 0, 0, 0.2)' : undefined}
      style={buttonStyle}
      tabIndex={isActive ? 0 : -1}
    />
  );

  if (DropdownContent) {
    return (
      <DropdownMenu.Root open={isOpen} onOpenChange={onDropdownStateChange}>
        <DropdownMenu.Trigger asChild>
          <NavigationBarButton
            size={20}
            iconSize={16}
            icon="ellipsis"
            title="More options"
            onPress={handlePress}
            color={isActive ? colors.accent_text_color : colors.text_color_primary}
            iconColor={isActive ? colors.accent_text_color : colors.text_color_primary}
            hoverBackgroundColor='rgba(0, 0, 0, 0.2)'
            style={buttonStyle}
            tabIndex={isActive ? 0 : -1}
          />
        </DropdownMenu.Trigger>
        {DropdownContent}
      </DropdownMenu.Root>
    );
  }

  return ellipsisButton;
});

EllipsisButton.propTypes = {
  isActive: PropTypes.bool,
  isOpen: PropTypes.bool,
  colors: PropTypes.object.isRequired,
  onPress: PropTypes.func,
  DropdownContent: PropTypes.node,
  onDropdownStateChange: PropTypes.func,
};

EllipsisButton.displayName = 'EllipsisButton';

const SidebarListItemLayout = React.forwardRef(({
  title,
  subtitle,
  icon,
  customIcon,
  isActive = false,
  isRightContainerVisible,
  indentation = 0,
  rightIcon,
  onPress,
  children,
  dropdownContent: DropdownContent,
  noEllipsis = false,
  isWarning = false,
  onDropdownStateChange,
  style,
  ...props
}, ref) => {
  const { colors, accentBackgroundColor } = useTheme();
  const { isXS } = useResponsiveness();
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownJustClosedRef = useRef(false);

  // Memoize styles
  const styles = useMemo(
    () => createStyles(colors, isXS, subtitle, isActive, isWarning, noEllipsis),
    [colors, isXS, subtitle, isActive, isWarning, noEllipsis]
  );

  // Memoize handlers
  const handleDropdownStateChange = useCallback((open) => {
    setIsDropdownOpen(open);
    if (!open) {
      dropdownJustClosedRef.current = true;
      setTimeout(() => { dropdownJustClosedRef.current = false; }, 200);
    }
    onDropdownStateChange?.(open);
  }, [onDropdownStateChange]);

  const handleOuterPress = useCallback((e) => {
    if (isDropdownOpen || dropdownJustClosedRef.current) return;
    onPress?.(e);
  }, [onPress, isDropdownOpen]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);
  const handleFocus = useCallback(() => setIsFocused(true), []);
  const handleBlur = useCallback(() => setIsFocused(false), []);
  // Pointer and focus hover state is desktop-only. On touch, the first tap
  // focuses the row and synthesizes mouseenter; setState here re-renders
  // (hover highlight) and RN-web drops the click.
  const pointerHighlightHandlers = SUPPORTS_HOVER
    ? {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        onHoverIn: handleMouseEnter,
        onHoverOut: handleMouseLeave,
        onFocus: handleFocus,
        onBlur: handleBlur,
      }
    : null;

  // Memoize indentation style
  const indentationStyle = useMemo(() => ({
    paddingLeft: indentation === -1 ? 0 : !!indentation ? indentation * 20 : 8,
  }), [indentation]);

  // The "..." action slot is forced visible (independent of hover) for the
  // active row, while its menu is open, and on touch-only web — where
  // hide-until-hover would steal the first tap (see dropdown.css).
  const forceActionsVisible =
    isActive || isDropdownOpen || isRightContainerVisible || (isWeb && !SUPPORTS_HOVER);

  // Memoize right container style.
  //
  // On web we deliberately DO NOT drive opacity from JS pointer state here.
  // Hovering the ellipsis was flaky because onHoverIn/onHoverOut
  // (pointerenter/pointerleave) can be dropped during fast cursor movement
  // or pre-empted when the Radix menu trigger grabs pointer capture, which
  // made the button flicker / disappear right as the user reached for it.
  // Instead the reveal is handled purely by CSS :hover / :focus-within on the
  // row (see [data-sidebar-row] / [data-sidebar-actions] in dropdown.css),
  // which the browser never misses. data-sidebar-actions="visible" forces it
  // on for the active row and while the menu is open.
  const rightContainerStyle = useMemo(() => ({
    ...styles.rightContainer,
    ...(isWeb
      ? null
      : {
          opacity: Platform.OS === 'macos'
            ? 1
            : (isHovered || forceActionsVisible || isFocused ? 1 : 0),
        }),
  }), [styles.rightContainer, isWeb, isHovered, isFocused, forceActionsVisible]);

  // Ensure stale open state is cleared when ellipsis is hidden or dropdown content is removed
  useEffect(() => {
    if (noEllipsis || !DropdownContent) {
      setIsDropdownOpen(false);
      onDropdownStateChange?.(false);
    }
  }, [noEllipsis, DropdownContent, onDropdownStateChange]);

  return (
    <View
      {...pointerHighlightHandlers}
      style={styles.container}
      {...(isWeb ? { dataSet: { sidebarRow: 'true' } } : null)}
    >
      <View 
        style={[
          styles.inner,
          indentationStyle,
          (isHovered || isFocused) && { backgroundColor: isWarning ? undefined : colors.solid_color_tertiary },
          isActive && styles.active,
        ]}
      >
        <Pressable
          ref={ref}
          onPress={handleOuterPress}
          // Expo Link supplies a style prop even when no custom style is set.
          // Merge it so it cannot replace the icon/title row layout.
          style={StyleSheet.flatten([
            { flex: 1, minWidth: 0, height: '100%', flexDirection: 'row', alignItems: 'center' },
            style,
          ])}
          accessibilityRole="button"
          accessibilityState={{ selected: isActive }}
          tabIndex={0}
          {...props}
        >
        <ItemIcon
          icon={icon}
          customIcon={customIcon}
          isActive={isActive}
          colors={colors}
          accentBackgroundColor={accentBackgroundColor}
          isWarning={isWarning}
          styles={styles}
          isXS={isXS}
        />
        
        <TitleContent
          title={title}
          subtitle={subtitle}
          isActive={isActive}
          colors={colors}
          styles={styles}
        />
        </Pressable>

        <View
          style={rightContainerStyle}
          {...(isWeb
            ? { dataSet: { sidebarActions: forceActionsVisible ? 'visible' : 'hidden' } }
            : null)}
        >
          {rightIcon}
          {!noEllipsis && (
            <EllipsisButton
              isActive={isActive}
              isOpen={isDropdownOpen}
              colors={colors}
              isXS={isXS}
              // onPress={handlePress}
              DropdownContent={DropdownContent}
              onDropdownStateChange={handleDropdownStateChange}
            />
          )}
        </View>
        {children}
      </View>
    </View>
  );
});

SidebarListItemLayout.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.string,
  customIcon: PropTypes.node,
  isActive: PropTypes.bool,
  indentation: PropTypes.number,
  rightIcon: PropTypes.node,
  onPress: PropTypes.func,
  children: PropTypes.node,
  dropdownContent: PropTypes.node,
  noEllipsis: PropTypes.bool,
  isWarning: PropTypes.bool,
  onDropdownStateChange: PropTypes.func,
};

SidebarListItemLayout.displayName = 'SidebarListItemLayout';

export default React.memo(SidebarListItemLayout, (prevProps, nextProps) => {
  // Custom comparison function for memoization
  return (
    prevProps.title === nextProps.title &&
    prevProps.subtitle === nextProps.subtitle &&
    prevProps.icon === nextProps.icon &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.indentation === nextProps.indentation &&
    prevProps.noEllipsis === nextProps.noEllipsis &&
    prevProps.isWarning === nextProps.isWarning &&
    prevProps.isRightContainerVisible === nextProps.isRightContainerVisible &&
    prevProps.rightIcon === nextProps.rightIcon &&
    prevProps.dropdownContent === nextProps.dropdownContent &&
    prevProps.style === nextProps.style &&
    prevProps.onPress === nextProps.onPress
  );
});
